import { React, useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import LeaseApiService from '../../services/apiService';
import { AsyncPaginate } from 'react-select-async-paginate';
import { useCurrency } from '../../../INVENTORY/Layouts/currencyContext';
import { useTranslation } from 'react-i18next';

const MasterDetails = ({
  tableLoading,
  setTransectionData,
  bedRoomList,
  setunitCategoryList,
  setcommunityList,
  setCityList,
  setFeaturesList,
  transectionData,
  countriesList,
  cityList,
  propertyList,
  salersList,
  landlordList,
  communityList,
  unitTypeList,
  unitViewList,
  unitCategoryList,
  floorList,
  isEdit,
  statusText,
}) => {
  const { t } = useTranslation();
  const apiService = new LeaseApiService();
  const ITEMS_PER_PAGE = 100;
  const { currencyPrecision } = useCurrency();
  const SQFT_TO_SQM = 0.092903;
  const areaUnitOptions = [
    { value: 'SqFt', label: t('unitMaster.masterDetailsFields.sqFt') },
    { value: 'SqM', label: t('unitMaster.masterDetailsFields.sqM') },
  ];
  const [totalAreaUnit, setTotalAreaUnit] = useState(areaUnitOptions[0]);
  const convertArea = (value, unit) => {
    const num = Number(value) || 0;
    if (!num) return (0).toFixed(3);
    return unit?.value === 'SqM' ? (num / SQFT_TO_SQM).toFixed(3) : (num * SQFT_TO_SQM).toFixed(3);
  };

  const handleAmountBlur = (e) => {
    const { name, value } = e.target;
    const decimals = currencyPrecision?.decimalNumbers || 2;
    const num = value === '' || value === null || isNaN(Number(value)) ? 0 : Number(value);
    setTransectionData((prev) => ({ ...prev, [name]: num.toFixed(decimals) }));
  };
  const formatDisplay = (value) => {
    if (value === '' || value === null || value === undefined || Number(value) === 0) {
      return (0).toFixed(currencyPrecision?.decimalNumbers || 2);
    }
    return value;
  };

  // Pre-lowercase once per landlordList load instead of on every keystroke over 40k+ records
  const searchableLandlordList = useMemo(
    () =>
      (landlordList || []).map((tenant) => ({
        ...tenant,
        _valueLower: String(tenant?.value ?? '').toLowerCase(),
        _textLower: String(tenant?.text ?? '').toLowerCase(),
      })),
    [landlordList],
  );

  const handleInputChange = (input) => {
    const name = input?.target?.name || input.name;
    const value = input?.target?.value || input.value;

    setTransectionData((prev) => {
      const updatedData = { ...prev, [name]: value };
      return updatedData;
    });
  };
  const handleDropDownChange = async ({ name, value }) => {
    if (name === 'property') {
      const propertyNo = value?.value?.value;
      if (!propertyNo) {
        // Property cleared: Country, City and Community come from the property, so they go with it.
        setFeaturesList([]);
        setCityList([]);
        setcommunityList([]);
        setTransectionData((prev) => ({ ...prev, property: null, country: null, city: null, community: null }));
        return;
      }
      const scndparams = new URLSearchParams();
      scndparams.append('Action', 'GetUnitfeatuersByPropertyNo');
      scndparams.append('PropertyNo', propertyNo);
      const scndRes = await apiService.getApiParamater('UNIT_RA_COMMON', `?${scndparams.toString()}`);
      // Without the fallback, a failed/empty response sets this to undefined,
      // and UnitFeature's `featuresList?.reduce(...)` then evaluates to
      // undefined (not {}), crashing Object.keys(undefined) on render.
      setFeaturesList(scndRes?.data?.result?.data || []);

      const countParam = new URLSearchParams();
      countParam.append('Action', 'UMCountryCityByPropertyNo');
      // The property being chosen now (not the previous one in state) decides the country and city.
      countParam.append('PropertyNo', propertyNo);
      const cntryRes = await apiService.getApiParamater('UNIT_RA_COMMON', `?${countParam.toString()}`);
      const found = cntryRes?.data?.result?.data;
      const propertyLocation = Array.isArray(found) ? found[0] : found;
      const countryName = propertyLocation?.Country ?? propertyLocation?.country ?? '';
      const cityName = propertyLocation?.City ?? propertyLocation?.city ?? '';

      // Communities depend on the property's country and city.
      let communities = [];
      if (countryName) {
        const comParam = new URLSearchParams();
        comParam.append('DropdownFor', 'UMCommunitiesByCountryCity');
        comParam.append('CascadValue', countryName);
        if (cityName) comParam.append('CascadText', cityName);
        const commRes = await apiService.getApiParamater('UNIT_RA_CASCAD', `?${comParam.toString()}`);
        communities = commRes?.data?.items || [];
      }
      setcommunityList(communities);
      setTransectionData((prev) => ({
        ...prev,
        country: countryName ? { label: countryName, value: countryName } : null,
        city: cityName ? { label: cityName, value: cityName } : null,
        community: null,
        [name]: value,
      }));
    } else if (name === 'country') {
      if (!value?.label) {
        setCityList([]);
        setcommunityList([]);
        setTransectionData((prev) => ({
          ...prev,
          country: null,
          city: null,
          community: null,
        }));
        return;
      }
      const scndparams = new URLSearchParams();
      scndparams.append('DropdownFor', 'UMCitiesByCountry');
      scndparams.append('CascadValue', value?.label);
      const scndRes = await apiService.getApiParamater('UNIT_RA_CASCAD', `?${scndparams.toString()}`);
      setCityList(scndRes?.data?.items || []);
      const comParam = new URLSearchParams();
      comParam.append('DropdownFor', 'UMCommunitiesByCountryCity');
      comParam.append('CascadValue', value?.label);
      const commRes = await apiService.getApiParamater('UNIT_RA_CASCAD', `?${comParam.toString()}`);
      setcommunityList(commRes?.data?.items || []);
      setTransectionData((prev) => ({
        ...prev,
        country: value,
        city: null,
        community: null,
      }));
      return;
    } else if (name === 'city') {
      const comParam = new URLSearchParams();
      comParam.append('DropdownFor', 'UMCommunitiesByCountryCity');
      comParam.append('CascadValue', transectionData?.country?.label);
      comParam.append('CascadText', value?.label);
      const commRes = await apiService.getApiParamater('UNIT_RA_CASCAD', `?${comParam.toString()}`);
      setcommunityList(commRes?.data?.items || []);
      setTransectionData((prev) => ({
        ...prev,
        city: value,
        community: null,
      }));
      return;
    } else if (name === 'unitType') {
      const comParam = new URLSearchParams();
      comParam.append('DropdownFor', 'UMUnitCategoriesByType');
      comParam.append('CascadValue', value?.label);
      const commRes = await apiService.getApiParamater('UNIT_RA_CASCAD', `?${comParam.toString()}`);
      setunitCategoryList(commRes?.data?.items);
    } else if (name === 'saller') {
      const matched = value ? landlordList?.find((tenant) => tenant?.value === value?.value) : null;
      setTransectionData((prev) => ({
        ...prev,
        saller: value,
        landlordId: matched ? { value: matched, label: matched?.value } : null,
      }));
      return;
    } else if (name === 'landlordId') {
      const matched = value?.value;
      setTransectionData((prev) => ({
        ...prev,
        landlordId: value,
        saller: matched ? { value: matched?.value, label: matched?.text } : null,
      }));
      return;
    }
    setTransectionData((prev) => ({ ...prev, [name]: value }));
  };
  const loadOptionsName = (search, loadedOptions, { page }) => {
    let filtered = searchableLandlordList;

    if (search) {
      const term = search.toLowerCase();
      filtered = searchableLandlordList.filter((tenant) => tenant._valueLower.includes(term) || tenant._textLower.includes(term));
    }

    const start = page * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;

    const optionsChunk = filtered.slice(start, end);

    return Promise.resolve({
      options: optionsChunk.map((tenant) => ({ value: tenant?.value, label: tenant.text })),
      hasMore: end < filtered.length,
      additional: { page: page + 1 },
    });
  };
  const loadOptionsId = (search, loadedOptions, { page }) => {
    let filtered = searchableLandlordList;

    if (search) {
      const term = search.toLowerCase();
      filtered = searchableLandlordList.filter((tenant) => tenant._valueLower.includes(term) || tenant._textLower.includes(term));
    }

    const start = page * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;

    const optionsChunk = filtered.slice(start, end);

    return Promise.resolve({
      options: optionsChunk.map((tenant) => ({ value: tenant, label: tenant?.value })),
      hasMore: end < filtered.length,
      additional: { page: page + 1 },
    });
  };
  return (
    <div className="cost-center-section my-3">
      <div className={`border-box2 p-3`}>
        {tableLoading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <div className="spinner-border text-primary" role="status" />
          </div>
        )}
        <div className="form-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.masterDetailsFields.resale')}
            </label>
            <input
              className="form-check-input"
              type="checkbox"
              id="resale"
              style={{ width: '20px', height: '20px' }}
              checked={transectionData.resale}
              onChange={(e) => setTransectionData((prev) => ({ ...prev, resale: e.target.checked }))}
            />
          </div>
          <div className="d-flex">
            {isEdit && statusText && statusText !== '0' && (
              <span className="fw-bold" style={{ color: statusText.toLowerCase().includes('block') ? 'red' : '#000' }}>
                {statusText}
              </span>
            )}
          </div>
          <div></div>
          <div className="d-flex">
            <label htmlFor="validationCustom18" className="form-label">
              {t('unitMaster.masterDetailsFields.property')} *
            </label>
            <Select
              className="p-0 w-100"
              theme={(theme) => ({
                ...theme,
                borderRadius: 5,
                padding: 9,
              })}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
                  color: state.isFocused ? '#000 ' : '#000',
                  ':hover': {
                    backgroundColor: '#F5F5F5',
                    color: '#000 ',
                  },
                }),
              }}
              value={transectionData?.property}
              onChange={(selectedOption) => handleDropDownChange({ name: 'property', value: selectedOption })}
              options={propertyList?.map((country) => ({ value: country, label: country?.text }))}
              isDisabled={isEdit}
            />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom18" className="form-label">
              {t('unitMaster.masterDetailsFields.sallerLandlord')}
            </label>
            <AsyncPaginate
              className="p-0 w-100"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#F5F5F5' : null,
                  color: '#000',
                  ':hover': { backgroundColor: '#F5F5F5', color: '#000' },
                }),
              }}
              loadOptions={loadOptionsName}
              debounceTimeout={300}
              defaultOptions
              value={transectionData?.saller}
              onChange={(selectedOption) => handleDropDownChange({ name: 'saller', value: selectedOption })}
              additional={{ page: 0 }}
              isDisabled={isEdit}
            />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom18" className="form-label">
              {t('unitMaster.masterDetailsFields.country')}
            </label>
            <Select
              className="p-0 w-100"
              theme={(theme) => ({
                ...theme,
                borderRadius: 5,
                padding: 9,
              })}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
                  color: state.isFocused ? '#000 ' : '#000',
                  ':hover': {
                    backgroundColor: '#F5F5F5',
                    color: '#000 ',
                  },
                }),
              }}
              value={transectionData?.country}
              onChange={(selectedOption) => handleDropDownChange({ name: 'country', value: selectedOption })}
              options={countriesList?.map((country) => ({ value: country, label: country?.text }))}
              placeholder={t('unitMaster.masterDetailsFields.fromProperty', 'Filled from the property')}
              isDisabled
            />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {' '}
              {t('unitMaster.masterDetailsFields.unitNo')} *
            </label>
            <input type="text" className="p-2 form-control w-75" disabled={isEdit} value={transectionData?.unitNo} onChange={handleInputChange} name="unitNo" />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom18" className="form-label">
              {t('unitMaster.masterDetailsFields.sallerLandlordId')}
            </label>
            <AsyncPaginate
              className="p-0 w-100"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#F5F5F5' : null,
                  color: '#000',
                  ':hover': { backgroundColor: '#F5F5F5', color: '#000' },
                }),
              }}
              loadOptions={loadOptionsId}
              debounceTimeout={300}
              defaultOptions
              value={transectionData?.landlordId}
              onChange={(selectedOption) => handleDropDownChange({ name: 'landlordId', value: selectedOption })}
              additional={{ page: 0 }}
              isDisabled={isEdit}
            />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom22" className="form-label">
              {t('unitMaster.masterDetailsFields.city')}
            </label>
            <Select
              className="p-0 w-100"
              theme={(theme) => ({
                ...theme,
                borderRadius: 5,
                padding: 9,
              })}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
                  color: state.isFocused ? '#000 ' : '#000',
                  ':hover': {
                    backgroundColor: '#F5F5F5',
                    color: '#000 ',
                  },
                }),
              }}
              value={transectionData?.city}
              onChange={(selectedOption) => handleDropDownChange({ name: 'city', value: selectedOption })}
              options={cityList?.map((country) => ({ value: country, label: country?.text }))}
              placeholder={t('unitMaster.masterDetailsFields.fromProperty', 'Filled from the property')}
              isDisabled
            />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom22" className="form-label">
              {t('unitMaster.masterDetailsFields.community')}
            </label>
            <Select
              className="p-0 w-100"
              theme={(theme) => ({
                ...theme,
                borderRadius: 5,
                padding: 9,
              })}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
                  color: state.isFocused ? '#000 ' : '#000',
                  ':hover': {
                    backgroundColor: '#F5F5F5',
                    color: '#000 ',
                  },
                }),
              }}
              value={transectionData?.community}
              onChange={(selectedOption) => handleDropDownChange({ name: 'community', value: selectedOption })}
              options={communityList?.map((country) => ({ value: country, label: country?.text }))}
              isDisabled={isEdit}
            />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.masterDetailsFields.municipalityAcNo')}{' '}
            </label>
            <input type="text" className="p-2 form-control w-75" value={transectionData?.municipltyNo} onChange={handleInputChange} name="municipltyNo" />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.masterDetailsFields.refNo')}{' '}
            </label>
            <input type="text" className="p-2 form-control w-75" disabled value={transectionData?.refNo} onChange={handleInputChange} name="refNo" />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.masterDetailsFields.unitClass')} *
            </label>
            <div className="d-flex gap-3 pt-1">
              {['Sale', 'Lease', 'PM'].map((label) => (
                <div className="form-check" key={label}>
                  <input
                    type="radio"
                    className="form-check-input"
                    name="unitClass"
                    id={`unitClass-${label}`}
                    value={label}
                    checked={(transectionData?.unitClass || 'Sale') === label}
                    onChange={() => handleInputChange({ name: 'unitClass', value: label })}
                  />
                  <label className="form-check-label" htmlFor={`unitClass-${label}`}>
                    {label === 'Sale' ? t('unitMaster.unitDetailsFields.sale') : label === 'Lease' ? t('unitMaster.unitDetailsFields.lease') : t('unitMaster.masterDetailsFields.pm')}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.masterDetailsFields.sector')}{' '}
            </label>
            <input type="text" className="p-2 form-control w-75" value={transectionData?.sector} onChange={handleInputChange} name="sector" />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.masterDetailsFields.block')}
            </label>
            <input type="text" className="p-2 form-control w-75" value={transectionData?.block} onChange={handleInputChange} name="block" />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom22" className="form-label">
              {t('unitMaster.masterDetailsFields.unitType')} *
            </label>
            <Select
              className="p-0 w-100"
              theme={(theme) => ({
                ...theme,
                borderRadius: 5,
                padding: 9,
              })}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
                  color: state.isFocused ? '#000 ' : '#000',
                  ':hover': {
                    backgroundColor: '#F5F5F5',
                    color: '#000 ',
                  },
                }),
              }}
              value={transectionData?.unitType}
              onChange={(selectedOption) => handleDropDownChange({ name: 'unitType', value: selectedOption })}
              options={unitTypeList?.map((country) => ({ value: country, label: country?.text }))}
            />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.unitDetailsFields.bathroom')}
            </label>
            <input type="text" className="p-2 form-control w-75" value={transectionData?.bathRoom} onChange={handleInputChange} name="bathRoom" />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.unitDetailsFields.balconyArea')}
            </label>
            <input type="text" className="p-2 form-control w-75" value={formatDisplay(transectionData?.balcony)} onChange={handleInputChange} onBlur={handleAmountBlur} name="balcony" />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.masterDetailsFields.unitCategory')} *
            </label>
            <Select
              className="p-0 w-100"
              theme={(theme) => ({
                ...theme,
                borderRadius: 5,
                padding: 9,
              })}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
                  color: state.isFocused ? '#000 ' : '#000',
                  ':hover': {
                    backgroundColor: '#F5F5F5',
                    color: '#000 ',
                  },
                }),
              }}
              value={transectionData?.unitCategory}
              onChange={(selectedOption) => handleDropDownChange({ name: 'unitCategory', value: selectedOption })}
              options={unitCategoryList?.map((country) => ({ value: country, label: country?.text }))}
            />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.masterDetailsFields.livingArea')}
            </label>
            <input type="text" className="p-2 form-control w-75" value={formatDisplay(transectionData?.livingarea)} onChange={handleInputChange} onBlur={handleAmountBlur} name="livingarea" />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.masterDetailsFields.terraceArea')}
            </label>
            <input type="text" className="p-2 form-control w-75" value={formatDisplay(transectionData?.terraceArea)} onChange={handleInputChange} onBlur={handleAmountBlur} name="terraceArea" />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.unitDetailsFields.unitView')} *
            </label>
            <Select
              className="p-0 w-100"
              theme={(theme) => ({
                ...theme,
                borderRadius: 5,
                padding: 9,
              })}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
                  color: state.isFocused ? '#000 ' : '#000',
                  ':hover': {
                    backgroundColor: '#F5F5F5',
                    color: '#000 ',
                  },
                }),
              }}
              value={transectionData?.unitView}
              onChange={(selectedOption) => handleDropDownChange({ name: 'unitView', value: selectedOption })}
              options={unitViewList?.map((country) => ({ value: country, label: country?.text }))}
            />
          </div>
          <div className="d-flex" style={{ gridColumn: 'span 2' }}>
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.unitDetailsFields.totalArea')} *
            </label>
            <input
              type="text"
              className="p-2 form-control"
              style={{ maxWidth: '140px' }}
              value={formatDisplay(transectionData?.totalArea)}
              onChange={handleInputChange}
              onBlur={handleAmountBlur}
              name="totalArea"
            />
            <Select
              className="p-0 mx-2"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                container: (base) => ({ ...base, minWidth: '110px' }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
                  color: '#000',
                  ':hover': { backgroundColor: '#F5F5F5', color: '#000' },
                }),
              }}
              value={totalAreaUnit}
              onChange={(selectedOption) => setTotalAreaUnit(selectedOption)}
              options={areaUnitOptions}
            />
            <input
              type="text"
              className="p-2 form-control"
              style={{ maxWidth: '140px' }}
              disabled
              value={convertArea(transectionData?.totalArea, totalAreaUnit)}
            />
            <label htmlFor="validationCustom01" className="form-label pt-1 center mx-2">
              {totalAreaUnit?.value === 'SqM' ? t('unitMaster.masterDetailsFields.sqFt') : t('unitMaster.masterDetailsFields.sqM')}
            </label>
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.masterDetailsFields.netArea')}
            </label>
            <input type="text" className="p-2 form-control w-75" value={formatDisplay(transectionData?.netArea)} onChange={handleInputChange} onBlur={handleAmountBlur} name="netArea" />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.unitDetailsFields.floor')}
            </label>
            <Select
              className="p-0 w-100"
              theme={(theme) => ({
                ...theme,
                borderRadius: 5,
                padding: 9,
              })}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
                  color: state.isFocused ? '#000 ' : '#000',
                  ':hover': {
                    backgroundColor: '#F5F5F5',
                    color: '#000 ',
                  },
                }),
              }}
              value={transectionData?.floor}
              onChange={(selectedOption) => handleDropDownChange({ name: 'floor', value: selectedOption })}
              options={floorList?.map((country) => ({ value: country, label: country?.text }))}
            />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.unitDetailsFields.noOfParkings')}
            </label>
            <input type="text" className="p-2 form-control w-75" value={transectionData?.parking} onChange={handleInputChange} name="parking" />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.masterDetailsFields.unitRate')}
            </label>
            <input type="text" className="p-2 form-control w-75" value={formatDisplay(transectionData?.unitRate)} onChange={handleInputChange} onBlur={handleAmountBlur} name="unitRate" />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('common.remarks')}
            </label>
            <textarea type="text" className="p-2 form-control w-75" value={transectionData?.remarks} onChange={handleInputChange} name="remarks" />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.unitDetailsFields.bedrooms')} *
            </label>
            <Select
              className="p-0 w-100"
              theme={(theme) => ({
                ...theme,
                borderRadius: 5,
                padding: 9,
              })}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
                  color: state.isFocused ? '#000 ' : '#000',
                  ':hover': {
                    backgroundColor: '#F5F5F5',
                    color: '#000 ',
                  },
                }),
              }}
              value={transectionData?.bedRooms}
              onChange={(selectedOption) => handleDropDownChange({ name: 'bedRooms', value: selectedOption })}
              options={bedRoomList?.map((country) => ({ value: country, label: country?.text }))}
            />
          </div>
          <div className="d-flex">
            <label htmlFor="validationCustom01" className="form-label pt-1 start">
              {t('unitMaster.unitDetailsFields.parkingPrice')}
            </label>
            <input type="text" className="p-2 form-control w-75" value={formatDisplay(transectionData?.parkingPrice)} onChange={handleInputChange} onBlur={handleAmountBlur} name="parkingPrice" />
          </div>
        </div>
      </div>
    </div>
  );
};
export default MasterDetails;
